import React from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import "./NewsBanner.css";

/* ------------ focus helpers: read focal point from block ------------- */
const clamp01 = (n) => Math.max(0, Math.min(1, Number(n)));
const parseMaybeJson = (v) => {
  if (!v) return null;
  if (typeof v === "string") {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
};

// prefer crop_json; else use last entry of image_json (array or string)
function getFocusJsonFromBlock(block) {
  if (!block) return null;
  if (block.crop_json) return parseMaybeJson(block.crop_json);

  const ij = block.image_json;
  if (!ij) return null;
  if (Array.isArray(ij)) return ij.length ? parseMaybeJson(ij[ij.length - 1]) : null;
  return parseMaybeJson(ij);
}

function objectPositionFromBlock(block) {
  const j = getFocusJsonFromBlock(block);
  const fp = j?.focalPoint;
  if (!fp || typeof fp.x !== "number" || typeof fp.y !== "number") {
    return "50% 50%";
  }
  const xPct = (clamp01(fp.x) * 100).toFixed(2);
  const yPct = (clamp01(fp.y) * 100).toFixed(2);
  return `${xPct}% ${yPct}%`;
}

function pickImageBlock(blocks) {
  if (!Array.isArray(blocks)) return null;
  // prefer an image that actually has focus/crop data
  return (
    blocks.find((b) => b.type === "image" && (b.crop_json || b.image_json)) ||
    blocks.find((b) => b.type === "image") ||
    null
  );
}

/* ---------- Markdown/link helpers ------------------------------- */
const ensureHttp = (href = "") => {
  if (!href) return href;
  if (
    href.startsWith("/") ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    /^https?:\/\//i.test(href)
  ) return href;
  if (href.startsWith("www.")) return `https://${href}`;
  return href;
};

// Keep links intact: take the first markdown paragraph
const firstParagraph = (md = "") => (md || "").split(/\n\s*\n/)[0];

const toISO = (d) => {
  if (!d) return undefined;
  const dt = d instanceof Date ? d : new Date(d);
  return isNaN(dt.getTime()) ? undefined : dt.toISOString();
};

/* ---------------- component ---------------- */
const NewsCardSmall = ({
  item,
  title,
  date,
  excerpt,
  image,
  news_id,
  variant = "banner",
}) => {
  const firstTextBlock = item?.blocks?.find((b) => b.type === "text");
  const mdFromItem = firstTextBlock?.body_md || firstTextBlock?.body || "";

  const imgBlock = pickImageBlock(item?.blocks);
  const imgPath = imgBlock?.path || item?.blocks?.find((b) => b.type === "image")?.path;

  const displayDateFromItem = item?.published_at
    ? new Date(item.published_at).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  const dateTimeISOFromItem = item?.published_at ? toISO(item.published_at) : undefined;

  const cardData = item
    ? {
        title: item.title,
        dateDisplay: displayDateFromItem,
        dateTimeISO: dateTimeISOFromItem,
        excerpt: mdFromItem, // no slicing; clamp in CSS
        image: imgPath,
        objectPosition: objectPositionFromBlock(imgBlock),
        news_id: item.news_id || item.id,
      }
    : {
        title,
        dateDisplay: date || null,
        dateTimeISO: toISO(date),
        excerpt,
        image,
        objectPosition: "50% 50%",
        news_id,
      };

  const isPageVariant = variant === "page";
  const cardClassName = isPageVariant ? "news-card" : "news-card-small news-card";
  const headerElement = isPageVariant ? "h3" : "h2";

  // Markdown components with safe/external-link behavior
  const mdComponents = {
    p: ({ node, ...props }) => (
      <p
        className={isPageVariant ? "news-card__excerpt" : "news-excerpt"}
        {...props}
      />
    ),
    a: ({ node, href, children, ...props }) => {
      const safeHref = ensureHttp(href || "");
      let isExternal = false;
      try {
        const base = typeof window !== "undefined" ? window.location.href : "http://localhost";
        const url = new URL(safeHref, base);
        isExternal = /^https?:$/i.test(url.protocol) &&
          (typeof window === "undefined" || url.origin !== window.location.origin);
      } catch {}
      return (
        <a
          href={safeHref}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          {...props}
        >
          {children}
        </a>
      );
    },
    // Disallow inline images in the excerpt
    img: () => null,
  };

  const excerptMd = firstParagraph(cardData.excerpt || "");

  return (
    <article className={cardClassName}>
      <header className={isPageVariant ? "news-card__header" : "news-header"}>
        {React.createElement(headerElement, {}, cardData.title)}
        {cardData.dateDisplay && (
          <time
            className={isPageVariant ? "date-badge" : "news-date"}
            dateTime={cardData.dateTimeISO || undefined}
          >
            {cardData.dateDisplay}
          </time>
        )}
      </header>

      {excerptMd && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          components={mdComponents}
        >
          {excerptMd}
        </ReactMarkdown>
      )}

      {cardData.image && (
        <div className={isPageVariant ? "news-card__img-wrapper" : ""}>
          <img
            src={cardData.image}
            alt={cardData.title || "news"}
            className={isPageVariant ? "" : "news-img"}
            loading="lazy"
            style={{ objectFit: "cover", objectPosition: cardData.objectPosition }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}

      <footer className={isPageVariant ? "" : "news-footer"}>
        <div className={isPageVariant ? "" : "news-btn-container"}>
          {cardData.news_id ? (
            <Link to={`/news/${cardData.news_id}`} className={isPageVariant ? "read-more" : "btn primary small"}>
              Read more
            </Link>
          ) : (
            <button className={isPageVariant ? "read-more" : "btn primary small"} type="button" disabled>
              Read more
            </button>
          )}
        </div>
      </footer>
    </article>
  );
};

export default NewsCardSmall;
